import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useExperiment } from '../../context/ExperimentContext';
import { db } from '../../firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';

export const QuestionnaireModal = ({ isOpen, requestId, userRole }) => {
  const { user } = useAuth();
  const { CURRENT_PHASE } = useExperiment();
  const [formData, setFormData] = useState({
    intrinsicScore: 0,
    extrinsicScore: 0,
    urgencyScore: 0,
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleScoreChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.intrinsicScore === 0 || formData.extrinsicScore === 0 || formData.urgencyScore === 0) {
      setError('Please answer all three questions to complete the session.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Save to /feedback with CORRECT field names (Score not Rating)
      await addDoc(collection(db, 'feedback'), {
        requestId,
        userId: user.uid,
        userRole,
        currentPhase: CURRENT_PHASE,
        intrinsicScore: formData.intrinsicScore,
        extrinsicScore: formData.extrinsicScore,
        urgencyScore: formData.urgencyScore,
        comment: formData.comment.trim(),
        submittedAt: serverTimestamp()
      });

      // Mark request as feedback submitted
      if (requestId) {
        await updateDoc(doc(db, 'requests', requestId), {
          feedbackSubmitted: true
        });
      }

      window.location.reload(); 
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to submit. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const LikertScale = ({ label, field }) => (
    <div className="form-group" style={{ marginBottom: '1.5rem' }}>
      <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 500, fontSize: '0.95rem', lineHeight: '1.4' }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
        {[1, 2, 3, 4, 5].map(num => (
          <button
            key={num}
            type="button"
            className={`likert-btn ${formData[field] === num ? 'active' : ''}`}
            onClick={() => handleScoreChange(field, num)}
          >
            {num}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
        <span>Strongly Disagree</span>
        <span>Strongly Agree</span>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" style={{ zIndex: 2000 }}>
      <Card className="questionnaire-modal" style={{ maxWidth: '600px', width: '90%', position: 'relative' }}>
        <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid #eee', paddingBottom: '1rem' }}>
          <h3 style={{ margin: 0, color: '#800000' }}>📝 Post-Session Feedback</h3>
          <p style={{ margin: '0.5rem 0 0', color: '#666', fontSize: '0.9rem' }}>
            Thank you for participating. Your responses are vital for this academic study.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <LikertScale 
            label="I participated in this session primarily because I genuinely enjoy supporting or learning with my peers." 
            field="intrinsicScore" 
          />

          <LikertScale 
            label="Points and system rewards were my primary reason for participating in this session." 
            field="extrinsicScore" 
          />

          <LikertScale 
            label="I felt this academic request required immediate assistance." 
            field="urgencyScore" 
          />

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Additional comments (Optional)</label>
            <textarea
              value={formData.comment}
              onChange={e => setFormData({...formData, comment: e.target.value})}
              placeholder="Share any thoughts on this tutoring experience..."
              rows="3"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem' }}
            />
          </div>

          {error && <p style={{ color: '#dc2626', fontSize: '0.9rem', marginTop: '1rem' }}>{error}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
            <Button type="submit" className="btn-primary" disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '8px' }}>
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
